import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ReferralsForm = () => {
  const [formData, setFormData] = useState({
    // Provider Information
    providerName: '',
    companyName: '',
    fax: '',
    phone: '',
    
    // Patient Demographics
    patientName: '',
    dob: '',
    sexAtBirth: '',
    address: '',
    patientPhone: '',
    email: '',
    insuranceCompany: '',
    memberId: '',
    groupNumber: '',
    
    // Services
    services: {
      medicationManagement: false,
      adhdTesting: false,
      autismScreening: false,
      tms: false,
      ketamine: false
    },
    
    // Location
    location: '',
    
    // First Responder/Military
    isFirstResponderOrMilitary: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [emailError, setEmailError] = useState('');
  
  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };
  
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name.startsWith('service-')) {
      const service = name.replace('service-', '');
      setFormData(prev => ({
        ...prev,
        services: {
          ...prev.services,
          [service]: checked
        }
      }));
    } else if (name === 'phone' || name === 'patientPhone') {
      // Format phone numbers
      const formattedPhone = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    } else if (name === 'email') {
      // Validate email
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      if (value && !validateEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError('');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email before submission
    if (formData.email && !validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/referrals', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          // Reset form
          providerName: '',
          companyName: '',
          fax: '',
          phone: '',
          patientName: '',
          dob: '',
          sexAtBirth: '',
          address: '',
          patientPhone: '',
          email: '',
          insuranceCompany: '',
          memberId: '',
          groupNumber: '',
          services: {
            medicationManagement: false,
            adhdTesting: false,
            autismScreening: false,
            tms: false,
            ketamine: false
          },
          location: '',
          isFirstResponderOrMilitary: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      window.scrollTo(0, 0);
    }
  };
  
  return (
    <>
      <Header />
      <div id="content" role="main">
        <div id="interior-header-container" className="container-fluid">
          <div className="container">
            <h1>Provider Referrals</h1>
          </div>
        </div>
        
        <div className="container py-5">
          {submitStatus === 'success' && (
            <div className="alert alert-success mb-4">
              <h4>Thank you for your referral!</h4>
              <p>Your referral has been submitted successfully. Our scheduling department will notify you once the patient is scheduled or if the patient declined to schedule.</p>
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="alert alert-danger mb-4">
              <h4>Submission Error</h4>
              <p>There was an error submitting the form. Please try again or contact us directly.</p>
            </div>
          )}
          
          <div className="bg-light p-4 rounded shadow-sm mb-5">
            <h2 className="mb-4 text-center">Referral Form</h2>
            <div className="alert alert-info mb-4" role="alert">
              <strong>Healthcare Providers Only:</strong> This form is exclusively for licensed healthcare providers referring patients to our practice. If you are a patient seeking services, please contact our office directly at <a href="tel:+14804718560" className="alert-link">(480) 471-8560</a> or use our <a href="/contact" className="alert-link">general contact form</a>.
            </div>

            <p className="text-center mb-4">
              Please complete this form to refer a patient to Redemption Psychiatry. Required fields are marked with an asterisk (*).
            </p>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <h3 className="mb-3" style={{ color: '#2D4F90' }}>Referring Provider Information</h3>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="providerName" className="form-label">Provider Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      id="providerName"
                      name="providerName"
                      value={formData.providerName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="companyName" className="form-label">Company Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="fax" className="form-label">Fax #</label>
                    <input
                      type="text"
                      className="form-control"
                      id="fax"
                      name="fax"
                      value={formData.fax}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="phone" className="form-label">Phone # <span className="text-danger">*</span></label>
                    <input
                      type="tel"
                      className="form-control"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      maxLength="14"
                      required
                    />
                  </div>
                </div>
                
                <div className="alert alert-info">
                  <small>Our scheduling department will notify you once the patient is scheduled or if the patient declined to schedule.</small>
                </div>
              </div>
              
              <div className="mb-5">
                <h3 className="mb-3" style={{ color: '#2D4F90' }}>Patient Demographics</h3>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="patientName" className="form-label">Patient's Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      id="patientName"
                      name="patientName"
                      value={formData.patientName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="dob" className="form-label">Date of Birth <span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      id="dob"
                      name="dob"
                      value={formData.dob}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-12">
                    <label className="form-label">Sex at Birth <span className="text-danger">*</span></label>
                    <div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          id="sexMale"
                          name="sexAtBirth"
                          value="Male"
                          checked={formData.sexAtBirth === 'Male'}
                          onChange={handleChange}
                          required
                        />
                        <label className="form-check-label" htmlFor="sexMale">Male</label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          id="sexFemale"
                          name="sexAtBirth"
                          value="Female"
                          checked={formData.sexAtBirth === 'Female'}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="sexFemale">Female</label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-12">
                    <label htmlFor="address" className="form-label">Address <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="patientPhone" className="form-label">Phone Number <span className="text-danger">*</span></label>
                    <input
                      type="tel"
                      className="form-control"
                      id="patientPhone"
                      name="patientPhone"
                      value={formData.patientPhone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      maxLength="14"
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="email" className="form-label">Email Address</label>
                    <input
                      type="email"
                      className={`form-control ${emailError ? 'is-invalid' : formData.email && !emailError ? 'is-valid' : ''}`}
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="example@domain.com"
                    />
                    {emailError && (
                      <div className="invalid-feedback">
                        {emailError}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label htmlFor="insuranceCompany" className="form-label">Insurance Company Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      id="insuranceCompany"
                      name="insuranceCompany"
                      value={formData.insuranceCompany}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="memberId" className="form-label">Member ID # <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      id="memberId"
                      name="memberId"
                      value={formData.memberId}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="groupNumber" className="form-label">Group #</label>
                    <input
                      type="text"
                      className="form-control"
                      id="groupNumber"
                      name="groupNumber"
                      value={formData.groupNumber}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              
              <div className="mb-5">
                <h3 className="mb-3" style={{ color: '#2D4F90' }}>Services Requested</h3>
                <p>What service/services is the patient being referred for? Check all that apply:</p>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="medicationManagement"
                        name="service-medicationManagement"
                        checked={formData.services.medicationManagement}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="medicationManagement">
                        Medication Management
                      </label>
                    </div>
                    
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="adhdTesting"
                        name="service-adhdTesting"
                        checked={formData.services.adhdTesting}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="adhdTesting">
                        ADHD Testing
                      </label>
                    </div>
                    
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="autismScreening"
                        name="service-autismScreening"
                        checked={formData.services.autismScreening}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="autismScreening">
                        Autism Screening
                      </label>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="tms"
                        name="service-tms"
                        checked={formData.services.tms}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="tms">
                        TMS (Transcranial Magnetic Stimulation)
                      </label>
                    </div>
                    
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="ketamine"
                        name="service-ketamine"
                        checked={formData.services.ketamine}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="ketamine">
                        Ketamine
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mb-5">
                <h3 className="mb-3" style={{ color: '#2D4F90' }}>Preferred Location</h3>
                <p>Preferred location to be seen: <span className="text-danger">*</span></p>
                
                <div className="mb-3">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="locationMesa"
                      name="location"
                      value="Mesa"
                      checked={formData.location === 'Mesa'}
                      onChange={handleChange}
                      required
                    />
                    <label className="form-check-label" htmlFor="locationMesa">
                      Mesa
                    </label>
                  </div>
                  
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="locationGilbert"
                      name="location"
                      value="Gilbert"
                      checked={formData.location === 'Gilbert'}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="locationGilbert">
                      Gilbert
                    </label>
                  </div>
                  
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="locationChandler"
                      name="location"
                      value="Chandler"
                      checked={formData.location === 'Chandler'}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="locationChandler">
                      Chandler
                    </label>
                  </div>
                  
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="locationPhoenix"
                      name="location"
                      value="Phoenix"
                      checked={formData.location === 'Phoenix'}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="locationPhoenix">
                      Phoenix
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="mb-5">
                <h3 className="mb-3" style={{ color: '#2D4F90' }}>First Responder/Military Status</h3>
                <p>
                  Is the patient a First Responder OR Military? <span className="text-danger">*</span><br />
                  <small className="text-muted">This includes Therapists & 911 dispatchers, Active or Retired!</small>
                </p>
                
                <div className="mb-3">
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="militaryYes"
                      name="isFirstResponderOrMilitary"
                      value="Yes"
                      checked={formData.isFirstResponderOrMilitary === 'Yes'}
                      onChange={handleChange}
                      required
                    />
                    <label className="form-check-label" htmlFor="militaryYes">Yes</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="militaryNo"
                      name="isFirstResponderOrMilitary"
                      value="No"
                      checked={formData.isFirstResponderOrMilitary === 'No'}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="militaryNo">No</label>
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-4">
                <button 
                  type="submit" 
                  className="button blue" 
                  disabled={isSubmitting}
                  style={{ minWidth: '200px' }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Referral'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ReferralsForm;